<template>
  <gas-page-shell :breadcrumb="['燃气协议管理', '协议解析管理']">
    <div class="mode-toolbar">
      <el-radio-group v-model="activeMode" size="small" class="mode-tabs" @change="onModeChange">
        <el-radio-button label="active_report">主动上报模式(独立应用场景类)</el-radio-button>
        <el-radio-button label="poll_dtu">召唤采集模式(DTU产品类)</el-radio-button>
        <el-radio-button label="mqtt">MQTT协议</el-radio-button>
      </el-radio-group>
      <div class="toolbar-actions">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreateProtocol">新增通讯协议</el-button>
        <el-button size="small" icon="el-icon-refresh" :loading="runtimeLoading" @click="reloadRuntime(false)">重载通讯服务</el-button>
        <el-button size="small" icon="el-icon-upload2" @click="triggerImport">导入</el-button>
        <el-button size="small" icon="el-icon-download" @click="exportProtocols">导出</el-button>
        <input ref="importInput" type="file" accept=".csv" class="hidden-file" @change="onImportFile" />
      </div>
    </div>

    <div class="runtime-panel">
      <div class="runtime-item">
        <span class="runtime-label">通讯服务</span>
        <el-tag size="mini" :type="runtimeStatus.running ? 'success' : 'info'">{{ runtimeStatus.running ? '运行中' : '未运行' }}</el-tag>
      </div>
      <div class="runtime-item">
        <span class="runtime-label">启用协议</span>
        <strong>{{ runtimeStatus.enabledProtocols || 0 }}</strong>
      </div>
      <div class="runtime-item">
        <span class="runtime-label">启用设备</span>
        <strong>{{ runtimeStatus.enabledDevices || 0 }}</strong>
      </div>
      <div class="runtime-item">
        <span class="runtime-label">UDP端口</span>
        <span>{{ runtimePorts(runtimeStatus.udpPorts) }}</span>
      </div>
      <div class="runtime-item">
        <span class="runtime-label">TCP端口</span>
        <span>{{ runtimePorts(runtimeStatus.tcpPorts) }}</span>
      </div>
      <div class="runtime-item">
        <span class="runtime-label">MQTT</span>
        <strong>{{ runtimeStatus.mqttConnected || 0 }}/{{ runtimeStatus.mqttClients || 0 }}</strong>
        <span class="runtime-sub">{{ (runtimeStatus.mqttConnected || 0) > 0 ? '已连接' : '未连接' }}</span>
      </div>
      <div class="runtime-item">
        <span class="runtime-label">MQTT绑定设备</span>
        <strong>{{ runtimeStatus.mqttBoundDevices || 0 }}</strong>
      </div>
      <div class="runtime-item runtime-wide">
        <span class="runtime-label">MQTT最后下发</span>
        <span>{{ runtimeStatus.lastMqttPublishTopic || '—' }}</span>
        <span v-if="runtimeStatus.lastMqttPublishTime" class="runtime-sub">{{ runtimeStatus.lastMqttPublishTime }}</span>
      </div>
      <div v-if="mqttClientIdText" class="runtime-item runtime-wide">
        <span class="runtime-label">MQTT ClientId</span>
        <span>{{ mqttClientIdText }}</span>
      </div>
      <div class="runtime-item runtime-wide">
        <span class="runtime-label">最后收包</span>
        <span>{{ runtimeStatus.lastMessageTime || '—' }}</span>
      </div>
      <el-button type="text" size="small" :loading="runtimeLoading" @click="loadRuntimeStatus">刷新状态</el-button>
    </div>
    <el-alert
      v-if="runtimeStatus.lastError"
      class="runtime-error"
      type="warning"
      :closable="false"
      show-icon
      :title="runtimeStatus.lastError"
    />

    <div class="filter-row">
      <el-form :inline="true" :model="query" size="small" class="search-form">
        <el-form-item label="协议类型">
          <el-select v-model="query.protocolType" clearable placeholder="全部" style="width:140px">
            <el-option v-for="item in protocolTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="通讯类型">
          <el-select v-model="query.commType" clearable placeholder="全部" style="width:120px">
            <el-option label="TCP" value="TCP" />
            <el-option label="UDP" value="UDP" />
            <el-option label="MQTT" value="MQTT" />
          </el-select>
        </el-form-item>
        <el-form-item label="关键词检索">
          <el-input v-model="query.keyword" clearable placeholder="请输入" style="width:200px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-title-row">
      <span class="table-title">协议列表 <em>共 {{ total }} 条记录</em></span>
      <gas-table-toolbar
        :columns="tableColumnDefs"
        :visible-map.sync="tableColumnVisible"
        :selected-count="selectedIds.length"
        :show-batch-delete="tableCanBatchDelete"
        @batch-delete="batchRemove"
      />
    </div>

    <el-table :data="list" v-loading="loading" border size="small" class="protocol-table" header-cell-class-name="protocol-table-header" :row-class-name="protocolRowClass" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="44" align="center" />
      <el-table-column prop="protocolCode" label="协议编号" min-width="110" show-overflow-tooltip />
      <el-table-column prop="protocolName" label="协议名称" min-width="180" show-overflow-tooltip />
      <el-table-column label="状态" width="88" align="center">
        <template slot-scope="{ row }">
          <el-tag
            size="small"
            :type="row.status === '1' ? 'danger' : 'success'"
            :effect="row.status === '1' ? 'dark' : 'light'"
            class="protocol-status-tag"
          >{{ row.status === '1' ? '停用' : '启用' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="协议类型" width="112" align="center" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.protocolType || '—' }}</template>
      </el-table-column>
      <el-table-column label="类型版本" min-width="132" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ protocolVersionLabel(row.protocolType, row.version) }}</template>
      </el-table-column>
      <el-table-column label="通讯类型" width="96" align="center">
        <template slot-scope="{ row }">{{ row.commType || 'TCP' }}</template>
      </el-table-column>
      <el-table-column label="使用端口号" width="108" align="center">
        <template slot-scope="{ row }">{{ row.port != null ? row.port : '—' }}</template>
      </el-table-column>
      <el-table-column prop="readCommand" label="读取命令" min-width="140" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.readCommand || '—' }}</template>
      </el-table-column>
      <el-table-column prop="readInterval" label="读取间隔" width="100" align="center" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.readInterval ? `${row.readInterval}秒` : '—' }}</template>
      </el-table-column>
      <el-table-column label="参数配置" width="96" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" class="link-set" @click="goParamSettings(row)">设置</el-button>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="协议说明" min-width="200" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.description || '—' }}</template>
      </el-table-column>
      <el-table-column label="可见范围" width="88" align="center">
        <template slot-scope="{ row }">
          <span class="scope-tag">{{ scopeLabel(row.scopeType) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="测试" width="72" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" class="op-test" @click="openParseTest(row)">测试</el-button>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="120" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" class="op-edit" @click="openEditProtocol(row)">修改</el-button>
          <el-button type="text" size="small" class="op-del" @click="removeRow(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :current-page="query.pageNum"
      :page-size="query.pageSize"
      :page-sizes="[10, 20, 50]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="640px" @closed="form = {}">
      <el-form :model="form" label-width="108px" size="small">
        <el-form-item label="协议编号" required>
          <el-input v-model="form.protocolCode" :disabled="!!form.protocolId" placeholder="如 4762、EY-Airbot" />
        </el-form-item>
        <el-form-item label="协议名称" required>
          <el-input v-model="form.protocolName" placeholder="请输入协议名称" />
        </el-form-item>
        <el-form-item label="协议类型" required>
          <el-select v-model="form.protocolType" style="width:100%" @change="onProtocolTypeChange">
            <el-option v-for="item in protocolTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="类型版本" required>
          <el-select
            v-model="form.version"
            filterable
            allow-create
            default-first-option
            style="width:100%"
            @change="onProtocolVersionChange"
          >
            <el-option v-for="item in protocolVersionOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="通讯类型">
          <el-select v-model="form.commType" style="width:100%">
            <el-option label="TCP" value="TCP" />
            <el-option label="UDP" value="UDP" />
            <el-option label="MQTT" value="MQTT" />
          </el-select>
        </el-form-item>
        <el-form-item label="应用模式">
          <el-select v-model="form.applyMode" style="width:100%">
            <el-option label="主动上报模式(独立应用场景类)" value="active_report" />
            <el-option label="召唤采集模式(DTU产品类)" value="poll_dtu" />
            <el-option label="MQTT协议" value="mqtt" />
          </el-select>
        </el-form-item>
        <el-form-item label="使用端口号">
          <el-input v-model.number="form.port" placeholder="0 表示不固定端口" />
        </el-form-item>
        <el-form-item label="服务器IP">
          <el-input v-model="form.serverIp" clearable placeholder="设备上报的目标服务器IP，MQTT协议可留空（自动取broker地址）" />
        </el-form-item>
        <el-form-item label="读取命令">
          <el-input
            v-model="form.readCommand"
            type="textarea"
            :rows="isMqttForm ? 6 : 2"
            :placeholder="readCommandPlaceholder"
          />
        </el-form-item>
        <el-form-item label="读取间隔">
          <el-input v-model="form.readInterval" placeholder="单位：秒，如 60">
            <template slot="append">秒</template>
          </el-input>
        </el-form-item>
        <el-form-item :label="form.commType === 'MQTT' || form.applyMode === 'mqtt' ? 'MQTT配置' : '高级配置'">
          <el-input
            v-model="form.settingsJson"
            type="textarea"
            :rows="4"
            :placeholder="settingsPlaceholder"
          />
        </el-form-item>
        <el-form-item label="可见范围">
          <el-radio-group v-model="form.scopeType">
            <el-radio label="public">公共</el-radio>
            <el-radio label="private">私有</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="协议说明">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="请输入协议说明" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width:100%">
            <el-option label="启用" value="0" />
            <el-option label="停用" value="1" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="testDialogTitle"
      :visible.sync="testVisible"
      width="920px"
      class="parse-test-dialog"
      @closed="onTestDialogClosed"
    >
      <div v-if="testProtocol" class="test-tip">
        <template v-if="isMqttTest">
          MQTT 按参数配置的 JSON 路径+名称解析。数组字段（如 datas）中每个元素会展开为一条记录，根级字段会复制到每条记录；ts 等时间字段作为采集时间入库。
        </template>
        <template v-else>
          将原始报文（JSON 或 HEX，支持多行）粘贴到下方。市电透传远传按整包原文和参数配置解析，不做 7B/55 解封装。市电/压力远传宏电帧按内置寄存器解析；市电压力远传会同时解析 4 路流量计和 40091 起的压力模拟量。计算公式会在取值后再计算并带上单位。
        </template>
      </div>
      <el-input
        v-model="testPayload"
        type="textarea"
        :rows="10"
        :placeholder="parseTestPlaceholder"
        class="test-input"
      />
      <div class="test-actions">
        <el-button size="small" @click="testPayload = ''">清空</el-button>
        <el-button type="primary" size="small" :loading="testLoading" @click="runParseTest">开始解析</el-button>
      </div>
      <div v-if="testResult" class="test-result">
        <el-alert
          :title="testResult.message"
          :type="testResult.success ? 'success' : 'warning'"
          :closable="false"
          show-icon
          class="result-alert"
        />
        <div v-if="testResult.detectedFormat" class="format-tag">
          识别格式：<strong>{{ testResult.detectedFormat }}</strong>
          <span v-if="testResult.byteLength != null"> · 报文 {{ testResult.byteLength }} 字节</span>
          <span v-if="testResult.recordCount"> · {{ testResult.recordCount }} 条记录</span>
        </div>
        <el-table v-if="testItems.length" :data="testItems" border size="small" class="result-table">
          <el-table-column v-if="testHasRecords" prop="recordIndex" label="记录序号" width="88" align="center" />
          <el-table-column v-if="testHasRecords" prop="recordTime" label="采集时间" min-width="160">
            <template slot-scope="{ row }">{{ row.recordTime || '—' }}</template>
          </el-table-column>
          <el-table-column prop="paramName" label="参数名称" min-width="120" />
          <el-table-column v-if="testHasJsonPath" prop="jsonFullPath" label="JSON路径" min-width="140" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.jsonFullPath || row.jsonPath || '—' }}</template>
          </el-table-column>
          <el-table-column prop="metricKey" label="指标键" min-width="110" show-overflow-tooltip />
          <el-table-column prop="labelEn" label="原始标签" min-width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.labelEn || '—' }}</template>
          </el-table-column>
          <el-table-column prop="subDeviceCode" label="子设备" width="80" align="center">
            <template slot-scope="{ row }">{{ row.subDeviceCode || row.deviceCode || '—' }}</template>
          </el-table-column>
          <el-table-column prop="value" label="解析值" min-width="120" show-overflow-tooltip />
          <el-table-column prop="rawValue" label="原始值" min-width="100" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.rawValue || row.value || '—' }}</template>
          </el-table-column>
          <el-table-column prop="formula" label="计算公式" min-width="120" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.formula || '—' }}</template>
          </el-table-column>
          <el-table-column prop="unit" label="单位" width="80" align="center">
            <template slot-scope="{ row }">{{ row.unit || '—' }}</template>
          </el-table-column>
          <el-table-column label="状态" width="88" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="row.valid ? 'success' : 'danger'" size="mini">{{ row.valid ? '正常' : '异常' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="message" label="说明" min-width="140" show-overflow-tooltip />
        </el-table>
      </div>
      <span slot="footer">
        <el-button @click="testVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <protocol-param-settings-dialog
      :visible.sync="paramSettingsVisible"
      :protocol-id="paramSettingsProtocolId"
    />
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import ProtocolParamSettingsDialog from '@/views/protocol/components/ProtocolParamSettingsDialog.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import { gasApi } from '@/api/gas'

const PROTOCOL_TYPE_OPTIONS = [
  { label: '市电远传', value: '市电远传' },
  { label: '市电透传远传', value: '市电透传远传' },
  { label: '低功耗远传', value: '低功耗远传' },
  { label: '压力远传', value: '压力远传' },
  { label: '市电压力远传', value: '市电压力远传' },
  { label: '自定义类型', value: '自定义类型' }
]

const CITY_POWER_READ_COMMANDS = {
  city_power_14: '010300020014E405',
  city_power_28: '010300020028E414',
  city_power_66: '010300020042643B',
  city_power_86: '0103000200566434',
  pressure_default: '0103005A0008641F',
  city_pressure_full: '0103000200666420'
}

const PROTOCOL_VERSION_OPTIONS = {
  市电远传: [
    { label: '1v1', value: 'city_power_14' },
    { label: '1v2', value: 'city_power_28' },
    { label: '1v3', value: 'city_power_66' },
    { label: '1v4', value: 'city_power_86' }
  ],
  市电透传远传: [
    { label: '默认版本', value: 'passthrough_default' }
  ],
  低功耗远传: [
    { label: '常规版本', value: 'low_power_normal' },
    { label: '无工况版本', value: 'low_power_no_work' }
  ],
  压力远传: [
    { label: '默认版本', value: 'pressure_default' }
  ],
  市电压力远传: [
    { label: '1v1', value: 'city_power_14' },
    { label: '1v2', value: 'city_power_28' },
    { label: '1v3', value: 'city_power_66' },
    { label: '1v4', value: 'city_power_86' }
  ],
  自定义类型: [
    { label: '默认版本', value: 'custom_default' }
  ]
}

const emptyForm = () => ({
  status: '0',
  port: 0,
  serverIp: '',
  commType: 'TCP',
  applyMode: 'active_report',
  scopeType: 'public',
  protocolType: '市电远传',
  version: 'city_power_28',
  readCommand: '',
  readInterval: '',
  settingsJson: ''
})

export default {
  components: { GasPageShell, ProtocolParamSettingsDialog },
  mixins: [gasCrudPage],
  data() {
    return {
      idField: 'protocolId',
      listApi: gasApi.protocol.list,
      saveApi: gasApi.protocol.save,
      removeApi: gasApi.protocol.remove,
      batchRemoveApi: gasApi.protocol.batchRemove,
      activeMode: 'active_report',
      query: {
        pageNum: 1,
        pageSize: 10,
        keyword: '',
        status: '',
        protocolType: '',
        commType: '',
        applyModes: 'active_report'
      },
      testVisible: false,
      testProtocol: null,
      testPayload: '',
      testLoading: false,
      testResult: null,
      runtimeStatus: {},
      runtimeLoading: false,
      runtimeTimer: null,
      paramSettingsVisible: false,
      paramSettingsProtocolId: null,
      protocolTypeOptions: PROTOCOL_TYPE_OPTIONS
    }
  },
  created() {
    this.loadRuntimeStatus()
    this.runtimeTimer = setInterval(this.loadRuntimeStatus, 30000)
  },
  beforeDestroy() {
    if (this.runtimeTimer) clearInterval(this.runtimeTimer)
  },
  computed: {
    testDialogTitle() {
      if (!this.testProtocol) return '协议解析测试'
      return `协议解析测试 — ${this.testProtocol.protocolName}（${this.testProtocol.protocolCode}）`
    },
    settingsPlaceholder() {
      if (this.form && (this.form.commType === 'MQTT' || this.form.applyMode === 'mqtt')) {
        return '{"brokerUrl":"tcp://127.0.0.1:1883","topic":"gas/up/#","publishTopic":"gas/down/{deviceCode}"}'
      }
      if (this.form && this.form.protocolType === '低功耗远传') {
        return '{"lowPowerAck":true}'
      }
      return '{"dtuIdentityLength":11}'
    },
    isMqttForm() {
      return !!(this.form && (this.form.commType === 'MQTT' || this.form.applyMode === 'mqtt'))
    },
    isMqttTest() {
      const p = this.testProtocol
      if (!p) return false
      return String(p.commType || '').toLowerCase() === 'mqtt' || String(p.applyMode || '').toLowerCase() === 'mqtt'
    },
    parseTestPlaceholder() {
      if (this.isMqttTest) {
        return '粘贴设备上报的 JSON，例如：\n{"hv":"v0.1.27","datas":[{"ts":1789981507,"data":{"press":0.001}}],"battery":0.98,"cid":1789954460,"intensity":69}'
      }
      return '示例 JSON：\n{ "stdTotal": 125680, "temperature": 22.5 }\n\n示例宏电 DDP：\n7b09001032303233313230313035367b0103280001098700004fd94216494343a20c9600000000000000000000000000000000ffffffffffffffff4e37\n\n示例低功耗历史数据：\n553B00AA2000009F8C0280000806212001AA0000000000000000001604090A010A006FDC0000BAD600000000000000000000203ED34299096D1D55'
    },
    readCommandPlaceholder() {
      if (this.isMqttForm) {
        return '填写要下发的 JSON 文本，平台按原文发送，不做封装。可用 {deviceCode} 占位，例如 {"addr":"{deviceCode}","cmd":"read"}'
      }
      return '设备读取/召唤下发命令。市电透传远传按填写内容原样下发，不做 7B 封装。压力远传如 0103005A0008641F；市电远传如 010300020028E414，系统会自动封装 7B89 DDP'
    },
    protocolVersionOptions() {
      return PROTOCOL_VERSION_OPTIONS[(this.form && this.form.protocolType) || '市电远传'] || []
    },
    testItems() {
      return (this.testResult && this.testResult.items) || []
    },
    testHasRecords() {
      return this.testItems.some(row => row && row.recordIndex != null)
    },
    testHasJsonPath() {
      return this.testItems.some(row => row && (row.jsonFullPath || row.jsonPath || row.jsonName))
    },
    mqttClientIdText() {
      const ids = this.runtimeStatus && this.runtimeStatus.mqttClientIds
      if (!ids || !ids.length) return ''
      return ids.join('，')
    }
  },
  methods: {
    emptyForm,
    async loadRuntimeStatus() {
      this.runtimeLoading = true
      try {
        const res = await gasApi.protocol.runtimeStatus()
        this.runtimeStatus = res.data || {}
      } finally {
        this.runtimeLoading = false
      }
    },
    async reloadRuntime(silent) {
      this.runtimeLoading = true
      try {
        const res = await gasApi.protocol.runtimeReload()
        this.runtimeStatus = res.data || {}
        if (!silent) this.$message.success('通讯服务已重载')
      } finally {
        this.runtimeLoading = false
      }
    },
    runtimePorts(ports) {
      return ports && ports.length ? ports.join('、') : '—'
    },
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => {
        if (p[k] === '') p[k] = undefined
      })
      return p
    },
    async loadData() {
      if (!this.listApi) return
      this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    onModeChange(mode) {
      this.query.applyModes = mode || this.activeMode
      this.query.pageNum = 1
      this.loadData()
    },
    openCreateProtocol() {
      const mqtt = this.activeMode === 'mqtt'
      this.openCreate({
        ...emptyForm(),
        applyMode: this.activeMode,
        commType: mqtt ? 'MQTT' : 'TCP',
        protocolType: mqtt ? '自定义类型' : '市电远传',
        version: mqtt ? 'custom_default' : 'city_power_28',
        readCommand: mqtt ? '' : CITY_POWER_READ_COMMANDS.city_power_28
      })
    },
    openEditProtocol(row) {
      const protocolType = this.normalizeProtocolType(row.protocolType)
      this.openEdit({
        ...row,
        protocolType,
        version: this.normalizeProtocolVersion(protocolType, row.version)
      }, emptyForm())
      // 从 settingsJson 中回填服务器IP到独立输入框
      this.form.serverIp = this.parseSettingsObject(row.settingsJson).serverIp || ''
    },
    parseSettingsObject(text) {
      if (!text || !String(text).trim()) return {}
      try {
        const v = JSON.parse(String(text))
        return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
      } catch (e) {
        return {}
      }
    },
    mergeServerIpIntoSettings() {
      const ip = (this.form.serverIp || '').trim()
      if (!this.form.settingsJson || !String(this.form.settingsJson).trim()) {
        this.form.settingsJson = ip ? JSON.stringify({ serverIp: ip }) : ''
        return
      }
      try {
        const obj = JSON.parse(String(this.form.settingsJson))
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
          if (ip) this.$message.warning('高级配置不是合法JSON，服务器IP未并入，请检查高级配置内容')
          return
        }
        if (ip) obj.serverIp = ip
        else delete obj.serverIp
        this.form.settingsJson = JSON.stringify(obj, null, 2)
      } catch (e) {
        if (ip) this.$message.warning('高级配置不是合法JSON，服务器IP未并入，请检查高级配置内容')
      }
    },
    resetSearch() {
      this.activeMode = 'active_report'
      this.query = {
        pageNum: 1,
        pageSize: this.query.pageSize,
        keyword: '',
        status: '',
        protocolType: '',
        commType: '',
        applyModes: 'active_report'
      }
      this.loadData()
    },
    scopeLabel(scope) {
      return scope === 'private' ? '私有' : '公共'
    },
    protocolRowClass({ row }) {
      return row && row.status === '1' ? 'protocol-row-disabled' : ''
    },
    protocolVersionLabel(type, version) {
      const normalizedType = this.normalizeProtocolType(type)
      const normalized = this.normalizeProtocolVersion(normalizedType, version)
      const options = PROTOCOL_VERSION_OPTIONS[normalizedType] || PROTOCOL_VERSION_OPTIONS[type] || []
      const hit = options.find(o => o.value === normalized || o.value === version || o.label === version)
      return hit ? hit.label : (version || '—')
    },
    onProtocolTypeChange(type) {
      const options = PROTOCOL_VERSION_OPTIONS[type] || []
      if (!options.length) return
      if (!options.some(o => o.value === this.form.version || o.label === this.form.version)) {
        this.form.version = options[0].value
      }
      this.onProtocolVersionChange(this.form.version)
    },
    onProtocolVersionChange(version) {
      if (this.isMqttForm) return
      const type = this.form && this.form.protocolType
      if (type !== '市电远传' && type !== '压力远传' && type !== '市电压力远传') return
      const current = (this.form.readCommand || '').replace(/\s+/g, '').toUpperCase()
      const known = Object.keys(CITY_POWER_READ_COMMANDS).map(k => CITY_POWER_READ_COMMANDS[k].toUpperCase())
      const next = CITY_POWER_READ_COMMANDS[version]
      if (!next) return
      if (!current || known.includes(current)) {
        this.form.readCommand = next
      }
    },
    goParamSettings(row) {
      this.paramSettingsProtocolId = row.protocolId
      this.paramSettingsVisible = true
    },
    async submitForm() {
      if (!this.form.protocolName || !this.form.protocolCode) {
        return this.$message.warning('请填写协议编号和协议名称')
      }
      if (!this.form.protocolType || !this.form.version) {
        return this.$message.warning('请选择协议类型和类型版本')
      }
      if (this.form.applyMode === 'mqtt') {
        this.form.commType = 'MQTT'
      }
      // 服务器IP并入 settingsJson（后端协议表无独立IP列，复用高级配置JSON存储）
      this.mergeServerIpIntoSettings()
      await gasCrudPage.methods.submitForm.call(this)
      this.reloadRuntime(true)
    },
    triggerImport() {
      this.$refs.importInput && this.$refs.importInput.click()
    },
    async onImportFile(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      const text = await file.text()
      const lines = text.split(/\r?\n/).filter(l => l.trim())
      if (lines.length < 2) return this.$message.warning('CSV 至少需要表头与一行数据')
      const headers = this.parseCsvLine(lines[0])
      let ok = 0
      let paramOk = 0
      for (let i = 1; i < lines.length; i++) {
        const cols = this.parseCsvLine(lines[i])
        const row = {}
        headers.forEach((h, idx) => { row[h] = (cols[idx] || '').trim() })
        const code = row.protocolCode || row['协议编号']
        const name = row.protocolName || row['协议名称']
        if (!code || !name) continue
        try {
          const savedRes = await gasApi.protocol.save({
            protocolCode: code,
            protocolName: name,
            protocolType: this.normalizeProtocolType(row.protocolType || row['协议类型']),
            commType: row.commType || row['通讯类型'] || 'TCP',
            applyMode: this.normalizeApplyMode(row.applyMode || row['应用模式'] || this.activeMode),
            port: Number(row.port || row['端口号'] || 0) || 0,
            version: this.normalizeProtocolVersion(
              this.normalizeProtocolType(row.protocolType || row['协议类型']),
              row.version || row['版本'] || row['类型版本']
            ),
            readCommand: row.readCommand || row['读取命令'] || '',
            readInterval: row.readInterval || row['读取间隔'] || '',
            scopeType: row.scopeType === 'private' || row['可见范围'] === '私有' ? 'private' : 'public',
            status: row.status === '1' || row['状态'] === '停用' ? '1' : '0',
            description: row.description || row['协议说明']
          })
          ok++
          const protocolId = (savedRes.data || {}).protocolId
          const params = this.parseImportedParams(row['参数配置(JSON)'])
          if (protocolId && params.length) {
            paramOk += await this.importProtocolParams(protocolId, params)
          }
        } catch (err) {
          /* skip duplicate or invalid */
        }
      }
      this.$message.success(`导入完成，成功 ${ok} 条协议，${paramOk} 条参数`)
      this.loadData()
    },
    parseCsvLine(line) {
      const out = []
      let cur = ''
      let inQ = false
      for (let i = 0; i < line.length; i++) {
        const c = line[i]
        if (c === '"') {
          if (inQ && line[i + 1] === '"') {
            cur += '"'
            i++
          } else {
            inQ = !inQ
          }
          continue
        }
        if (c === ',' && !inQ) {
          out.push(cur)
          cur = ''
          continue
        }
        cur += c
      }
      out.push(cur)
      return out.map(s => s.trim())
    },
    normalizeApplyMode(v) {
      if (!v) return this.activeMode
      if (v.includes('MQTT') || v === 'mqtt') return 'mqtt'
      if (v.includes('DTU') || v.includes('召唤') || v === 'poll_dtu') return 'poll_dtu'
      if (v === 'active_report' || v === 'poll_dtu' || v === 'mqtt') return v
      return 'active_report'
    },
    normalizeProtocolType(v) {
      const text = (v || '').trim()
      const hit = PROTOCOL_TYPE_OPTIONS.find(o => o.value === text || o.label === text)
      if (hit) return hit.value
      if (text.includes('自定义')) return '自定义类型'
      if (text.includes('透传')) return '市电透传远传'
      if (text.includes('低功耗')) return '低功耗远传'
      if (text.includes('市电') && text.includes('压力')) return '市电压力远传'
      if (text.includes('压力')) return '压力远传'
      return '市电远传'
    },
    normalizeProtocolVersion(type, v) {
      const text = (v || '').trim()
      const options = PROTOCOL_VERSION_OPTIONS[type] || []
      const hit = options.find(o => o.value === text || o.label === text)
      if (hit) return hit.value
      if (type === '低功耗远传' && text.includes('无工况')) return 'low_power_no_work'
      if (type === '低功耗远传') return text || 'low_power_normal'
      if (type === '压力远传') return text || 'pressure_default'
      if (type === '自定义类型') return text || 'custom_default'
      if (type === '市电透传远传') return text || 'passthrough_default'
      if (type === '市电远传' || type === '市电压力远传') {
        const n = text.toLowerCase()
        if (n === 'city_pressure_full') return 'city_power_86'
        if (n === '1v4' || n.includes('86') || n.includes('city_power_86')) return 'city_power_86'
        if (n === '1v3' || n.includes('66') || n.includes('city_power_66')) return 'city_power_66'
        if (n === '1v1' || n.includes('14') || n.includes('city_power_14')) return 'city_power_14'
        if (n === '1v2' || n.includes('28') || n.includes('18') || n.includes('city_power_28')) return 'city_power_28'
        return text || 'city_power_28'
      }
      return text || (options[0] && options[0].value) || '1.0'
    },
    async exportProtocols() {
      const res = await gasApi.protocol.list({ ...this.buildQuery(), pageNum: 1, pageSize: 10000 })
      let rows = (res.data || {}).records || []
      const selected = this.selectedIds || []
      if (selected.length) {
        rows = rows.filter(r => selected.includes(r.protocolId))
      }
      const paramMap = await this.exportProtocolParams(rows)
      const headerCn = ['协议编号', '协议名称', '协议类型', '类型版本', '通讯类型', '应用模式', '端口号', '读取命令', '读取间隔', '可见范围', '状态', '协议说明', '参数配置(JSON)']
      const csv = [headerCn.join(',')]
      const modeLabel = { active_report: '主动上报', poll_dtu: '召唤采集', mqtt: 'MQTT' }
      rows.forEach(r => {
        csv.push([
          r.protocolCode,
          r.protocolName,
          r.protocolType || '',
          this.protocolVersionLabel(r.protocolType, r.version),
          r.commType || 'TCP',
          modeLabel[r.applyMode] || r.applyMode,
          r.port != null ? r.port : 0,
          r.readCommand || '',
          r.readInterval || '',
          r.scopeType === 'private' ? '私有' : '公共',
          r.status === '1' ? '停用' : '启用',
          r.description || '',
          JSON.stringify(paramMap[r.protocolId] || [])
        ].map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(','))
      })
      const blob = new Blob(['\ufeff' + csv.join('\n')], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '通讯协议列表.csv'
      link.click()
      URL.revokeObjectURL(link.href)
      this.$message.success(`已导出 ${rows.length} 条`)
    },
    async exportProtocolParams(rows) {
      const map = {}
      await Promise.all((rows || []).map(async r => {
        try {
          const res = await gasApi.protocol.paramList({ protocolId: r.protocolId, pageNum: 1, pageSize: 1000 })
          const records = ((res.data || {}).records) || []
          map[r.protocolId] = records
            .slice()
            .sort((a, b) => Number(a.sortNo || 0) - Number(b.sortNo || 0))
            .map(p => ({
              paramName: p.paramName,
              unit: p.unit || '',
              dataType: p.dataType || 'numeric',
              paramCategory: p.paramCategory || '',
              startByte: p.startByte,
              byteCount: p.byteCount,
              parseMethod: p.parseMethod || '',
              jsonPath: p.jsonPath || '',
              jsonName: p.jsonName || '',
              calcFormula: p.calcFormula || '',
              subsetSegments: p.subsetSegments || '',
              maxValue: p.maxValue,
              minValue: p.minValue,
              sortNo: p.sortNo
            }))
        } catch (e) {
          map[r.protocolId] = []
        }
      }))
      return map
    },
    parseImportedParams(text) {
      if (!text || !String(text).trim()) return []
      try {
        const parsed = JSON.parse(text)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    async importProtocolParams(protocolId, params) {
      try {
        const existing = await gasApi.protocol.paramList({ protocolId, pageNum: 1, pageSize: 1000 })
        const records = ((existing.data || {}).records) || []
        for (const p of records) {
          await gasApi.protocol.paramRemove(p.paramId)
        }
      } catch (e) {
        /* 忽略默认参数清理失败，继续导入 */
      }
      let count = 0
      for (const p of params) {
        if (!p || !p.paramName) continue
        try {
          await gasApi.protocol.paramSave({
            protocolId,
            paramName: p.paramName,
            unit: p.unit || '',
            dataType: p.dataType || 'numeric',
            paramCategory: p.paramCategory || '',
            startByte: p.startByte,
            byteCount: p.byteCount,
            parseMethod: p.parseMethod || '',
            jsonPath: p.jsonPath || '',
            jsonName: p.jsonName || '',
            calcFormula: p.calcFormula || '',
            subsetSegments: p.subsetSegments || '',
            maxValue: p.maxValue,
            minValue: p.minValue,
            sortNo: p.sortNo
          })
          count++
        } catch (e) {
          /* 忽略单条参数导入失败 */
        }
      }
      return count
    },
    openParseTest(row) {
      this.testProtocol = row
      this.testPayload = ''
      this.testResult = null
      this.testVisible = true
    },
    onTestDialogClosed() {
      this.testProtocol = null
      this.testPayload = ''
      this.testResult = null
    },
    async runParseTest() {
      if (!this.testProtocol) return
      if (!this.testPayload.trim()) return this.$message.warning('请粘贴待测试的原始数据')
      this.testLoading = true
      try {
        const res = await gasApi.protocol.parseTest(this.testProtocol.protocolId, { payload: this.testPayload })
        this.testResult = res.data || {}
      } finally {
        this.testLoading = false
      }
    }
  }
}
</script>

<style scoped>
.mode-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: #f7f8fa;
  border-radius: 4px;
  margin-bottom: 16px;
}
.mode-toolbar .el-button + .el-button {
  margin-left: 0;
}
.hidden-file {
  display: none;
}
.mode-tabs {
  flex: 1;
  min-width: 0;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.runtime-panel {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 18px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  background: #fff;
  color: #4e5969;
  font-size: 13px;
}
.runtime-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.runtime-wide {
  min-width: 190px;
}
.runtime-sub {
  margin-left: 8px;
  color: #86909c;
}
.runtime-label {
  color: #86909c;
}
.runtime-error {
  margin-bottom: 12px;
}
.mode-tabs >>> .el-radio-button__inner {
  padding: 9px 14px;
  font-size: 13px;
}
.filter-row {
  margin-bottom: 16px;
}
.search-form >>> .el-form-item {
  margin-bottom: 0;
}
.protocol-table >>> .protocol-table-header {
  background: #4e5969 !important;
  color: #fff;
  font-weight: 500;
}
.protocol-table >>> th.protocol-table-header .cell {
  color: #fff;
}
.protocol-table >>> .protocol-row-disabled td {
  background: #fff1f0 !important;
}
.protocol-status-tag {
  min-width: 52px;
  text-align: center;
  font-weight: 600;
}
.name-link {
  color: #1677ff;
  text-decoration: none;
}
.name-link:hover {
  text-decoration: underline;
}
.link-set {
  color: #1677ff;
  font-weight: 500;
}
.scope-tag {
  color: #4e5969;
  font-size: 13px;
}
.op-edit {
  color: #1677ff;
}
.op-test {
  color: #722ed1;
  font-weight: 500;
}
.op-del {
  color: #f53f3f;
}
.test-tip {
  font-size: 13px;
  color: #86909c;
  margin-bottom: 10px;
  line-height: 1.5;
}
.test-input >>> textarea {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 13px;
}
.test-actions {
  margin: 12px 0 16px;
  text-align: right;
}
.result-alert {
  margin-bottom: 12px;
}
.format-tag {
  font-size: 13px;
  color: #4e5969;
  margin-bottom: 10px;
}
.result-table {
  margin-top: 8px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
</style>
